import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Button,
  SearchInput,
  SideDrawer,
  TableChip,
  CustomSelect,
  ConfirmDeleteModal,
  SkeletonText,
} from '@/components';
import { V2Table } from '@/common/components';
import { PlusIcon } from '../components/Icons';
import { DashboardPage } from '../components/PremiumDesign';
import { EmailBuilderContainer } from '../components/email-builder/EmailBuilderContainer';
import { MarketplaceEmailPreviewDrawer } from '../components/marketplace/MarketplaceEmailPreviewDrawer';
import {
  getGmailTemplates,
  createGmailTemplate,
  updateGmailTemplate,
  deleteGmailTemplate,
  getMarketplaceIndustries,
  getMarketplaceEmailTemplates,
  getMarketplaceEmailTemplateDetail,
} from '../api';
import { parseApiError } from '@/common/utils';
import { encodeId } from '@/common/utils/encryption';
import editIcon from '../assets/editIcon.png';
import trashIcon from '../assets/trashIcon.png';
import sendIcon from '../assets/Send.png';
import eyeIcon from '../assets/eye.png';
import { SendEmailCampaignPage } from '../components/SendEmailCampaignPage';
import { EmailPhonePreview } from '../components/EmailPhonePreview';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // ignore parse errors
  }
  return localStorage.getItem('company_id');
}

// ─── Delete Confirm Modal removed in favor of common component ────────────────

// ─── Send Campaign Drawer ─────────────────────────────────────────────────────

// Removed SendCampaignDrawer, moved to SendEmailCampaignPage.jsx

// ─── Main Page ────────────────────────────────────────────────────────────────

export function EmailTemplatePage() {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 'list' | 'create' | 'edit'
  const [viewMode, setViewMode] = useState('list');
  // eslint-disable-next-line no-unused-vars
  const [sourceTab, setSourceTab] = useState('my-templates'); // 'my-templates' | 'marketplace'
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [viewingMarketplaceTemplate, setViewingMarketplaceTemplate] =
    useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [loadingMarketplaceId, setLoadingMarketplaceId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Fetch Company Templates ──────────────────────────────────────────────
  const {
    data: templatesRaw,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['gmail-templates', companyId, debouncedSearch],
    queryFn: () => getGmailTemplates(companyId, { search: debouncedSearch }),
    enabled: !!companyId && sourceTab === 'my-templates',
  });

  // Non-blocking error toast
  useEffect(() => {
    if (isError && sourceTab === 'my-templates') {
      const { message } = parseApiError(error);
      toast.error(message || 'Failed to load email templates.');
    }
  }, [isError, error, sourceTab]);

  const templates = useMemo(() => {
    return Array.isArray(templatesRaw)
      ? templatesRaw
      : templatesRaw?.results || [];
  }, [templatesRaw]);

  // ── Fetch Marketplace Industries ─────────────────────────────────────────
  const { data: industriesRaw = [], isLoading: isIndustriesLoading } = useQuery(
    {
      queryKey: ['marketplace-industries'],
      queryFn: () => getMarketplaceIndustries(),
      enabled: sourceTab === 'marketplace',
      staleTime: 60_000,
    },
  );

  const industries = useMemo(() => {
    if (Array.isArray(industriesRaw)) return industriesRaw;
    return industriesRaw?.results || [];
  }, [industriesRaw]);

  const industryOptions = useMemo(() => {
    return [
      { value: '', label: 'All Industries' },
      ...industries.map((ind) => ({
        value: String(ind.id),
        label: ind.name,
      })),
    ];
  }, [industries]);

  // ── Fetch Marketplace Templates ──────────────────────────────────────────
  const {
    data: marketplaceRaw,
    isLoading: isMarketplaceLoading,
    isError: isMarketplaceError,
    error: marketplaceError,
  } = useQuery({
    queryKey: [
      'marketplace-email-templates',
      companyId,
      selectedIndustry,
      debouncedSearch,
    ],
    queryFn: () =>
      getMarketplaceEmailTemplates(companyId, {
        industry_id: selectedIndustry || undefined,
        q: debouncedSearch || undefined,
      }),
    enabled: !!companyId && sourceTab === 'marketplace',
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isMarketplaceError && sourceTab === 'marketplace') {
      const { message } = parseApiError(marketplaceError);
      toast.error(message || 'Failed to load marketplace templates.');
    }
  }, [isMarketplaceError, marketplaceError, sourceTab]);

  const marketplaceTemplates = useMemo(() => {
    if (!marketplaceRaw) return [];
    if (Array.isArray(marketplaceRaw)) return marketplaceRaw;
    return marketplaceRaw?.results || [];
  }, [marketplaceRaw]);

  // ── Create Mutation ──────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload) => createGmailTemplate({ companyId, payload }),
    onSuccess: () => {
      toast.success('Email template created successfully!');
      queryClient.invalidateQueries({
        queryKey: ['gmail-templates', companyId],
      });
      setViewMode('list');
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to create template. Please try again.');
    },
  });

  // ── Update Mutation ──────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: ({ templateId, payload }) =>
      updateGmailTemplate({ companyId, templateId, payload }),
    onSuccess: () => {
      toast.success('Email template updated successfully!');
      queryClient.invalidateQueries({
        queryKey: ['gmail-templates', companyId],
      });
      setViewMode('list');
      setEditingTemplate(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update template. Please try again.');
    },
  });

  // ── Delete Mutation ──────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (templateId) => deleteGmailTemplate({ companyId, templateId }),
    onSuccess: () => {
      toast.success('Template deleted.');
      queryClient.invalidateQueries({
        queryKey: ['gmail-templates', companyId],
      });
      setDeleteTarget(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete template.');
      setDeleteTarget(null);
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSave = (formData) => {
    if (viewMode === 'edit' && editingTemplate) {
      updateMutation.mutate({
        templateId: editingTemplate.id,
        payload: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setViewMode('edit');
  };

  const handleUseMarketplaceTemplate = async (template) => {
    try {
      setLoadingMarketplaceId(template.id);
      let detail = template;
      try {
        const fullDetail = await getMarketplaceEmailTemplateDetail(
          companyId,
          template.id,
        );
        if (fullDetail) detail = fullDetail;
      } catch {
        // Fallback to card summary data if detail endpoint fails
      }

      let parsedDesign = detail.design_json;
      if (typeof parsedDesign === 'string') {
        try {
          parsedDesign = JSON.parse(parsedDesign);
        } catch {
          parsedDesign = [];
        }
      }

      setEditingTemplate({
        name: detail.name ? `${detail.name}` : 'New Template',
        subject: detail.subject || '',
        category: detail.category || 'marketing',
        design_json: Array.isArray(parsedDesign) ? parsedDesign : [],
        body: detail.body || '',
        variables: detail.variables || [],
        // id undefined guarantees createMutation (POST /gmail/templates/) is called
        id: undefined,
      });
      setViewMode('create');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to open template in builder.');
    } finally {
      setLoadingMarketplaceId(null);
    }
  };

  const handleCancel = () => {
    setViewMode('list');
    setEditingTemplate(null);
    setViewingTemplate(null);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const myColumns = [
    {
      id: 'id',
      label: 'ID',
      defaultWidth: 72,
      minWidth: 56,
      resizable: false,
      align: 'center',
      skeleton: <SkeletonText width="w-8" className="mx-auto" />,
      cell: (template) => (
        <span className="text-xs text-gray-400 font-mono">#{template.id}</span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 280,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-32" />,
      cell: (template) => (
        <span
          className="truncate block text-sm font-medium text-gray-800"
          title={template.name}
        >
          {template.name}
        </span>
      ),
    },
    {
      id: 'subject',
      label: 'Subject',
      defaultWidth: 360,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-40" />,
      cell: (template) => (
        <span
          className="truncate block text-sm text-gray-500"
          title={template.subject || ''}
        >
          {template.subject || <em className="text-gray-300">No subject</em>}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultWidth: 160,
      minWidth: 140,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-24" className="mx-auto" />,
      cell: (template) => (
        <div className="flex items-center justify-center gap-1 sm:gap-1.5">
          <button
            id={`email-template-view-btn-${template.id}`}
            onClick={() => setViewingTemplate(template)}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-all focus:outline-none shrink-0"
            title="View Template"
          >
            <img
              src={eyeIcon}
              alt="View"
              className="w-5 h-5 object-contain shrink-0"
            />
          </button>
          <button
            id={`email-template-send-btn-${template.id}`}
            onClick={() => setSendTarget(template)}
            className="p-1.5 hover:bg-green-50 rounded-lg transition-all focus:outline-none shrink-0"
            title="Send Campaign"
          >
            <img
              src={sendIcon}
              alt="Send"
              className="w-5 h-5 object-contain shrink-0"
            />
          </button>
          <button
            id={`email-template-edit-btn-${template.id}`}
            onClick={() => handleEdit(template)}
            className="p-1.5 hover:bg-blue-50 rounded-lg transition-all focus:outline-none shrink-0"
            title="Edit Template"
          >
            <img
              src={editIcon}
              alt="Edit"
              className="w-5 h-5 object-contain shrink-0"
            />
          </button>
          <button
            id={`email-template-delete-btn-${template.id}`}
            onClick={() => setDeleteTarget(template)}
            className="p-1.5 hover:bg-red-50 rounded-lg transition-all focus:outline-none shrink-0"
            title="Delete Template"
          >
            <img
              src={trashIcon}
              alt="Delete"
              className="w-5 h-5 object-contain shrink-0"
            />
          </button>
        </div>
      ),
    },
  ];

  const marketplaceColumns = [
    {
      id: 'name',
      label: 'Template Name',
      defaultWidth: 320,
      minWidth: 180,
      resizable: true,
      skeleton: <SkeletonText width="w-40" />,
      cell: (template) => (
        <div className="flex flex-col min-w-0">
          <span
            className="font-semibold text-text-primary truncate block text-sm"
            title={template.name}
          >
            {template.name}
          </span>
          <span
            className="text-xs text-text-muted truncate block mt-0.5"
            title={template.subject}
          >
            Subject: {template.subject || '(No subject)'}
          </span>
        </div>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      defaultWidth: 160,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-20" />,
      cell: (template) => (
        <span className="text-sm text-text-secondary capitalize">
          {template.category || 'Marketing'}
        </span>
      ),
    },
    {
      id: 'industry',
      label: 'Industry Category',
      defaultWidth: 180,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-24" />,
      cell: (template) => (
        <TableChip tone="neutral" className="capitalize">
          {template.industry_name || template.industry?.name || 'General'}
        </TableChip>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultWidth: 180,
      minWidth: 150,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-24" className="mx-auto" />,
      cell: (template) => {
        const isUsing = loadingMarketplaceId === template.id;
        return (
          <div className="flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              id={`marketplace-preview-btn-${template.id}`}
              onClick={() => setViewingMarketplaceTemplate(template)}
              className="p-1.5 rounded-md hover:bg-black/5 transition-colors shrink-0"
              title="Preview Template"
            >
              <img
                src={eyeIcon}
                alt="Preview"
                className="w-5 h-5 min-w-[20px] min-h-[20px] object-contain shrink-0"
              />
            </button>
            <Button
              id={`marketplace-use-btn-${template.id}`}
              variant="primary"
              onClick={() => handleUseMarketplaceTemplate(template)}
              disabled={Boolean(loadingMarketplaceId)}
              isLoading={isUsing}
              className="h-[30px] px-3 text-xs font-semibold shrink-0"
            >
              <span>Use Template</span>
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DashboardPage
      className={
        viewMode !== 'list' && viewMode !== 'marketplace'
          ? '!overflow-hidden !p-2 sm:!p-3 h-full min-h-0 flex-1'
          : ''
      }
    >
      {/* ── Delete Confirm Modal ──────────────────────────────────────────── */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title="Delete Template"
        itemName={deleteTarget?.name}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        isPending={deleteMutation.isPending}
      />

      {/* ── Send Campaign Page ──────────────────────────────────────────── */}
      {sendTarget && (
        <SendEmailCampaignPage
          template={sendTarget}
          companyId={companyId}
          onCancel={() => setSendTarget(null)}
          onSend={(data) => {
            setSendTarget(null);
            queryClient.invalidateQueries({
              queryKey: ['gmail-templates', companyId],
            });
            const campaignId = data?.id || data?.campaign_id;
            if (campaignId) {
              navigate(`/email-campaigns/${encodeId(campaignId)}`);
            } else {
              navigate('/email-campaigns');
            }
          }}
        />
      )}

      {/* ── List View ────────────────────────────────────────────────────── */}
      {viewMode === 'list' && !sendTarget && (
        <div className="email-templates-page flex flex-col w-full mx-auto">
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto sm:flex-1">
              <SearchInput
                id="email-template-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  sourceTab === 'marketplace'
                    ? 'Search marketplace templates…'
                    : 'Search by name or subject…'
                }
                className="w-full sm:max-w-xs"
              />

              {/* Marketplace button beside the searchbar - hidden for now */}
              {/* <Button
                id="email-template-marketplace-btn"
                variant={sourceTab === 'marketplace' ? 'primary' : 'secondary'}
                onClick={() => {  
                  setSourceTab(
                    sourceTab === 'marketplace'
                      ? 'my-templates'
                      : 'marketplace',
                  );
                  setSearchQuery('');
                }}
                className="h-[40px] px-3.5 shrink-0"
              >
                <span>
                  {sourceTab === 'marketplace' ? 'My Templates' : 'Marketplace'}
                </span>
              </Button> */}

              {/* Industry Filter Dropdown (Marketplace only) */}
              {sourceTab === 'marketplace' && (
                <CustomSelect
                  id="marketplace-industry-filter"
                  value={selectedIndustry}
                  onChange={(val) => setSelectedIndustry(val || '')}
                  options={industryOptions}
                  placeholder="All Industries"
                  disabled={isIndustriesLoading}
                  className="w-full sm:w-[190px] h-[40px] shrink-0"
                  buttonClassName="!h-[40px] w-full flex items-center justify-between rounded-xl bg-white border border-border-default px-3 text-sm font-medium !text-[#1F2241] [&>span]:!text-[#1F2241] focus:outline-none cursor-pointer"
                />
              )}
            </div>

            {/* Create Template Button (Only for My Templates) */}
            {sourceTab === 'my-templates' && (
              <Button
                id="email-template-create-btn"
                variant="primary"
                onClick={() => {
                  setEditingTemplate(null);
                  setViewMode('create');
                }}
                className="h-[40px] px-4 w-full sm:w-auto shrink-0"
                icon={<PlusIcon />}
              >
                <span>Create Template</span>
              </Button>
            )}
          </div>

          {/* Table */}
          {sourceTab === 'marketplace' ? (
            <V2Table
              tight
              columns={marketplaceColumns}
              data={isMarketplaceError ? [] : marketplaceTemplates}
              rowKey="id"
              storageKey="email_marketplace_templates_column_widths"
              loading={isMarketplaceLoading}
              empty={
                isMarketplaceError ? (
                  'Failed to load marketplace templates. Please try again.'
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm font-semibold text-text-primary">
                      No marketplace email templates found
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Try adjusting your search query or industry filter.
                    </p>
                  </div>
                )
              }
            />
          ) : (
            <V2Table
              tight
              columns={myColumns}
              data={templates}
              rowKey="id"
              storageKey="email_templates_column_widths"
              loading={isLoading}
              empty={
                <div className="flex flex-col items-center justify-center gap-2 py-6">
                  <p className="text-base font-medium text-gray-700">
                    {searchQuery
                      ? 'No templates match your search'
                      : 'No email templates yet'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {searchQuery
                      ? 'Try adjusting your search.'
                      : 'Click "Create Template" to build your first email.'}
                  </p>
                </div>
              }
            />
          )}
        </div>
      )}

      {/* ── Marketplace Preview Drawer ───────────────────────────────────── */}
      <MarketplaceEmailPreviewDrawer
        isOpen={Boolean(viewingMarketplaceTemplate)}
        onClose={() => setViewingMarketplaceTemplate(null)}
        template={viewingMarketplaceTemplate}
        onUseTemplate={(tpl) => {
          setViewingMarketplaceTemplate(null);
          handleUseMarketplaceTemplate(tpl);
        }}
        isUsing={Boolean(loadingMarketplaceId)}
      />

      {/* ── Create / Edit View (Email Builder) ───────────────────────────── */}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <EmailBuilderContainer
          key={editingTemplate?.id ?? 'new'}
          initialData={editingTemplate}
          onCancel={handleCancel}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      {/* ── View Template Drawer ───────────────────────────────────────────── */}
      <SideDrawer
        className="email-preview-panel"
        isOpen={!!viewingTemplate}
        onClose={() => setViewingTemplate(null)}
        width="md"
        showClose={false}
        headerClassName="email-preview-header !px-6 !py-5"
        bodyClassName="p-3 sm:p-4 overflow-y-auto visible-scrollbar"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <div className="flex flex-col text-left">
              <span className="text-[18px] leading-[26px] font-semibold text-[#1F2241] font-sf-pro-display">
                {viewingTemplate?.name || 'Template Preview'}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setViewingTemplate(null)}
                className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        }
      >
        {viewingTemplate && (
          <div className="flex justify-center items-start py-4 pb-8 w-full min-h-full">
            <div className="email-preview-phone-slot relative mx-auto">
              <div className="email-preview-phone-scale">
                <EmailPhonePreview template={viewingTemplate} />
              </div>
            </div>
          </div>
        )}
      </SideDrawer>
    </DashboardPage>
  );
}
